import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { authApi } from '../api/api';
import AuthBackground from '../components/AuthBackground';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await authApi.forgotPassword(emailOrUsername.trim());
      const email = response.data?.email || emailOrUsername.trim();
      setUserEmail(email);
      setSuccess(true);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('No account found with this email or username.');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to send reset code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    navigate('/reset-password', { state: { email: userEmail } });
  };

  return (
    <AuthBackground>
      {/* Card */}
      <div className="relative w-full max-w-[420px] mx-4 my-8">
        <div
          className="rounded-2xl"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.97)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Back button */}
          <div className="px-8 pt-6">
            <button
              onClick={() => navigate('/login')}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back to login
            </button>
          </div>

          {!success ? (
            <>
              {/* Header */}
              <div className="pt-4 pb-1 px-8 text-center">
                <div
                  className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
                  style={{
                    background: 'var(--action)',
                    boxShadow: '0 1px 3px color-mix(in srgb, var(--action) 30%, transparent)',
                  }}
                >
                  <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                  Forgot password?
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  No worries, we'll send you a reset code
                </p>
              </div>

              {/* Form */}
              <div className="px-8 pb-6 pt-5">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="emailOrUsername" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email or Username
                    </label>
                    <input
                      id="emailOrUsername"
                      type="text"
                      required
                      autoFocus
                      placeholder="Enter your email or username"
                      className="appearance-none block w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:border-transparent focus:bg-white transition-all duration-200"
                      value={emailOrUsername}
                      onChange={(e) => setEmailOrUsername(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  {error && (
                    <div role="alert" className="flex items-start gap-2 p-3 text-sm bg-red-50 border border-red-200 text-red-700 rounded-xl">
                      <svg className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd"/>
                      </svg>
                      <span>{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !emailOrUsername.trim()}
                    // Colour lives in classes, not the style object: an inline
                    // background outranks Tailwind's disabled: variants, so
                    // `disabled:opacity-50` faded the fill AND the label
                    // together and the button read as blank. This button starts
                    // disabled (unlike Login's, which only disables while
                    // submitting), so that state needs to stay legible.
                    className="w-full py-3 px-4 rounded-xl font-semibold text-sm text-center flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--focus-ring)] bg-[color:var(--action)] text-[color:var(--action-text)] hover:bg-[color:var(--action-hover)] disabled:bg-[color:var(--bg-sunken)] disabled:text-[color:var(--text-disabled)] disabled:cursor-not-allowed"
                    style={{
                      // Cleared whenever the button is disabled, not just while
                      // loading — this CTA starts disabled, and a saffron glow
                      // around a grey fill reads as a rendering fault.
                      boxShadow: (loading || !emailOrUsername.trim())
                        ? 'none'
                        : '0 1px 3px color-mix(in srgb, var(--action) 30%, transparent)',
                    }}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        Sending...
                      </span>
                    ) : 'Send Reset Code'}
                  </button>
                </form>

                <p className="text-center text-sm text-gray-500 mt-6">
                  Remember your password?{' '}
                  <a href="/login" className="font-semibold text-primary hover:underline transition-colors">
                    Sign in
                  </a>
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Success state */}
              <div className="px-8 pb-6 pt-4 text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h2>
                <p className="text-sm text-gray-500 mb-1">
                  We've sent a 6-digit verification code to:
                </p>
                <p className="font-semibold text-primary mb-6">{userEmail}</p>

                <div className="space-y-3">
                  <button
                    onClick={handleContinue}
                    className="w-full py-3 px-4 rounded-xl font-semibold text-sm text-white text-center flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--focus-ring)]"
                    style={{
                      background: 'var(--action)',
                      boxShadow: '0 1px 3px color-mix(in srgb, var(--action) 30%, transparent)',
                    }}
                  >
                    Enter Reset Code
                  </button>

                  <button
                    onClick={() => navigate('/login')}
                    className="w-full py-2.5 px-4 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    Back to Login
                  </button>
                </div>

                <p className="text-sm text-gray-500 mt-5">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button
                    onClick={() => { setSuccess(false); setEmailOrUsername(''); }}
                    className="font-semibold text-primary hover:underline transition-colors"
                  >
                    try again
                  </button>
                </p>
              </div>
            </>
          )}

          {/* Footer */}
          <div className="px-8 pb-6">
            <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400">
              <span>Powered by</span>
              <img src="/favicon-64.png" alt="Nextserve" className="h-4 w-4 rounded-sm" />
              <span className="font-medium text-gray-500">Nextserve</span>
            </div>
          </div>
        </div>
      </div>
    </AuthBackground>
  );
};

export default ForgotPassword;
